import { Grid, TextField } from '@mui/material';
import { ButtonCancel } from 'components/buttons';
import { ButtonApplyFilter } from 'components/buttons/ButtonApplyFilter';
import { MprcInputStyles } from 'components/filter/MprcInputStyles';
import { AutoCompleteDataModel } from 'components/MprcAutoComplete';
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { CoreFilterCommand, UserSettingsDto } from 'generated/CoreApi_v1';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkplaceAutoComplete } from 'views/foundation/workplace/components/WorkplaceAutoComplete';
import { IAdvFilterPickerProps, IAdvFilterResult } from '../interfaces';

////////////////////////////////////////////////////////////////////////////////

export const WorkplacePicker: React.FC<IAdvFilterPickerProps> = (props) => {

    const { filter, onApply, onCancel } = props;
    const { t } = useTranslation("shared");

    const [workplace, setWordplace] = useState<AutoCompleteDataModel[]>([]);
    const [searchText, setSearchText] = useState<string | null>(null);

    const settings = filter.pickerParams as UserSettingsDto | undefined;
    const label = settings?.workplaceLabel ?? t("AdvFilter.Picker.Workplace.WorkplaceText");
    const multiSelect = filter.canRepeat ?? false;
    const isWorkplaceFreeText = filter.command == CoreFilterCommand.UserWorkplaceText;
    const inputRef = useRef<HTMLInputElement>(undefined);

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const handleApply = () => {
        if (workplace.length > 0) {
            const newFilter = workplace.map(item => ({
                command: filter!.command,
                optionalGroup: filter?.optionGroup,
                text: `${filter?.displayName.replace("...", "")}: ${item.label}`,
                value: item.key,
                quickFilter: null,
                period: null,
                range: null,
                includeDescendants: null
            })) as IAdvFilterResult[];
            onApply(newFilter, filter);
        }
        else if (searchText != null) {
            const newFilter: IAdvFilterResult = {
                command: filter!.command,
                optionalGroup: filter?.optionGroup,
                text: `${filter?.displayName.replace("...", "")}: ${searchText}`,
                value: searchText,
                quickFilter: null,
                period: null,
                range: null,
                includeDescendants: null
            };
            onApply(newFilter, filter);
        }
    };

    useEffect(() => {
        if (inputRef) {
            (inputRef as any)?.current?.focus();
        }
    }, [inputRef]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <Grid container alignItems="flex-end" spacing={2} width="100%">
            <Grid size="grow">
                {
                    settings == undefined || settings?.isWorkplaceFreeText || isWorkplaceFreeText
                        ?
                        <TextField
                            fullWidth
                            autoFocus
                            value={searchText}
                            placeholder={label}
                            inputRef={inputRef}
                            onChange={(e) => setSearchText(e.currentTarget.value)}
                            onKeyDown={(e) => {
                                if (e.key == "Enter") handleApply();
                                else if (e.key == "Escape") onCancel();
                            }}
                            sx={MprcInputStyles.filter}
                        />
                        :
                        <WorkplaceAutoComplete
                            autoFocus
                            placeholder={label}
                            multiSelect={multiSelect}
                            inputRef={inputRef}
                            selectedItems={workplace}
                            onChange={(items) => setWordplace(items)}
                            onError={MprcRequestSnackbarErrorReport}
                            onApply={handleApply}
                            onCancel={onCancel}
                            sx={MprcInputStyles.filter}
                        />
                }
            </Grid>
            <Grid>
                <ButtonApplyFilter
                    variant="text"
                    disabled={settings == undefined || settings?.isWorkplaceFreeText ? searchText?.length == 0 : workplace.length == 0}
                    onClick={handleApply}
                />
                <ButtonCancel
                    variant="text"
                    onClick={onCancel}
                />
            </Grid>
        </Grid>
    );
};
